import { toast } from "sonner";
import { useState, useEffect } from "react";

import {
  Box,
  Table,
  alpha,
  Avatar,
  styled,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  IconButton,
  TableContainer,
} from "@mui/material";

import { useRouter } from "src/routes/hooks";

import { useBoolean } from "src/hooks/use-boolean";

import axiosInstance, { fetcher, endpoints } from "src/utils/axios";

import { warning } from "src/theme/core";
import { useTranslate } from "src/locales";
import useMemberships from "src/swr/use-memberships";

import { Label } from "src/components/label";
import { StarIcon, PencilIcon } from "src/components/custom/icons";
import { CustomBreadcrumbs } from "src/components/custom-breadcrumbs";
import { ConfirmationDialog } from "src/components/custom/confirmation-dialog";
import { LoadingSpinner } from "src/components/loading-screen/loading-spinner";

import { useAuthContext } from "src/auth/hooks";
import { setSession } from "src/auth/context/jwt";

import CompanyDialog from "./company-dialog";

const STATUS_COLORS = {
  Active: "success",
  "Pending Confirmation": "warning",
  Deactivated: "error",
  "Removed by company": "error",
  "User left company": "error",
};

export function CompaniesTable({ isDialog = false, setNotificationCount }) {
  const { t } = useTranslate('common');
  const { user, checkUserSession } = useAuthContext();
  const router = useRouter();

  const { data: memberships = [], isLoading, mutate: mutateMemberships } = useMemberships();

  const [selectedMembership, setSelectedMembership] = useState();

  const isSwitching = useBoolean(false);
  const splashScreen = useBoolean(false);
  const companyDialog = useBoolean(false);
  const deleteDialog = useBoolean(false);
  const isResending = useBoolean(false);
  const isDeleting = useBoolean(false);

  useEffect(() => {
    setNotificationCount?.(
      memberships.filter((m) => m.status === "Pending Confirmation").length
    );
  }, [memberships, setNotificationCount]);

  // Handlers
  const onAdd = () => {
    setSelectedMembership();
    companyDialog.onTrue();
  };

  const onEdit = (membership) => {
    setSelectedMembership(membership);
    companyDialog.onTrue();
  };

  const onDelete = (membership) => {
    setSelectedMembership(membership);
    deleteDialog.onTrue();
  };

  const onSelectConfirm = async (membership) => {
    try {
      if (membership?.companyId === user?.CompanyId) return;
      isSwitching.onTrue();
      const { data: res } = await axiosInstance.get(
        endpoints.auth.switchMembership(membership.id)
      );

      if (!res.access_token) throw new Error("No access token received");

      setSession(res.access_token);
      checkUserSession();

      splashScreen.onTrue();
      router.replace("/jobs");

      if (isDialog) router.refresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      splashScreen.onFalse();
      isSwitching.onFalse();
    }
  };

  const onDeleteConfirm = async () => {
    try {
      if (!selectedMembership) return;

      isDeleting.onTrue();
      const leavingCurrent = selectedMembership.companyId === user?.CompanyId;
      const { data: res } = await axiosInstance.get(
        endpoints.auth.deleteMembership(selectedMembership.id)
      );

      toast.success(res.message);
      mutateMemberships(prev => prev.filter(m => m.id !== selectedMembership.id), false);

      if (leavingCurrent) {
        setSession(res?.access_token);
        checkUserSession();

        splashScreen.onTrue();
        router.replace("/jobs");

        if (isDialog) router.refresh();
      }

    } catch (err) {
      toast.error(err.message);
    } finally {
      splashScreen.onFalse();
      isDeleting.onFalse();
    }
  };

  const onResend = async (membershipId) => {
    try {
      isResending.onTrue();
      const res = await fetcher(endpoints.staff.resendInvitation(membershipId));
      toast.success(res.message || "Invitation resent");
    } catch (err) {
      toast.error(err.message || "Failed to resend invitation");
    } finally {
      isResending.onFalse();
    }
  };

  return (
    <>
      <CompanyDialog
        open={companyDialog.value}
        onClose={companyDialog.onFalse}
        onExited={setSelectedMembership}
        companyId={selectedMembership?.companyId}
        mutate={mutateMemberships}
      />

      <ConfirmationDialog
        open={deleteDialog.value}
        onClose={deleteDialog.onFalse}
        onConfirm={onDeleteConfirm}
      />

      <CustomBreadcrumbs
        heading={t('companies')}
      // action={<AddButton variant="outlined" onClick={onAdd}>{t('add')}</AddButton>}
      />

      <TableContainer sx={{ minWidth: 1000, flex: 1 }}>
        <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 72 }} />
              <TableCell>{t('companyName')}</TableCell>
              <TableCell>{t('role')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell>{t('contactName')}</TableCell>
              <TableCell>{t('contactEmail')}</TableCell>
              <TableCell>{t('contactPhone')}</TableCell>
              <TableCell sx={{ width: 140 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {memberships.map((membership) => {
              const isCurrent = membership.companyId === user?.CompanyId;
              const isOwner = membership.role === "Owner"
              const canSwitch = membership.status === "Active";

              return (
                <TableRow
                  key={membership.id}
                  hover={!isCurrent && canSwitch}
                  onClick={() => canSwitch && onSelectConfirm(membership)}
                  sx={{
                    cursor: !isCurrent ? 'pointer' : 'default',
                    bgcolor: theme => isCurrent ? alpha(theme.palette.primary.main, 0.16) : 'inherit'
                  }}
                >
                  <StyledTableCell>
                    <Avatar src={membership.companyLogo} alt={membership.company}>
                      {membership.company.charAt(0).toUpperCase()}
                    </Avatar>
                  </StyledTableCell>
                  <StyledTableCell>{membership.company}</StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {membership.role}
                      {isOwner && <StarIcon width={18} color={warning.main} />}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Label color={STATUS_COLORS[membership.status] ?? "default"}>
                      {membership.status}
                    </Label>
                  </StyledTableCell>
                  <StyledTableCell>{membership.contactName}</StyledTableCell>
                  <StyledTableCell>{membership.contactEmail}</StyledTableCell>
                  <StyledTableCell>{membership.contactPhone}</StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" justifyContent="end" alignItems="center" gap={0.5}>
                      {isOwner && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(membership) }}>
                          <PencilIcon width={20} height={20} />
                        </IconButton>
                      )}
                      {/* <IconButton size="small" color="inherit" onClick={(e) => { e.stopPropagation(); onResend(membership.id) }}>
                        <OutgoingMailIcon width={20} height={20} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(membership) }}>
                        <TrashIcon width={20} height={20} />
                        </IconButton> */}
                    </Box>
                  </StyledTableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {isLoading && <LoadingSpinner sx={{ minHeight: 0.8 }} />}
      </TableContainer>

      {/* <Box
        position="sticky"
        top={0}
        zIndex={1}
        bgcolor="background.neutral"
        px={2}
        py={2}
        borderBottom={1}
        borderColor="divider"
      >
        <Box
          display="grid"
          gridTemplateColumns="50px repeat(6, 1fr) 30px 100px"
          gap={1}
          alignItems="center"
        >
          {["", t('companyName'), t('role'), t('status'), t('contactName'), t('contactEmail'), t('contactPhone'), "", ""].map(
            (title, i) => (
              <Typography key={i} variant="subtitle2" color="text.secondary">
                {title}
              </Typography>
            )
          )}
        </Box>
      </Box>

      <Box sx={{ height: "calc(100vh - 290px)", overflowY: "auto" }}>
        <List disablePadding>
          {isLoading ? (
            Array.from({ length: 6 }, (_, i) => (
              <ListItem key={i} disablePadding>
                <Box
                  display="grid"
                  gridTemplateColumns="50px repeat(6, 1fr) 30px"
                  gap={1}
                  py={1}
                  px={2}
                  width="100%"
                >
                  <Skeleton variant="circular" width={32} height={32} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton height={20} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </ListItem>
            ))
          ) : (
            data.map((membership) => {
              const isCurrent = membership.companyId === user?.CompanyId;
              const canSwitch = membership.status === "Active";

              return (
                <Box key={membership.id} display="flex" gap={1}>
                  <ListItemButton
                    selected={isCurrent}
                    onClick={() => onSelectConfirm(membership)}
                    disabled={isSwitching.value || !canSwitch}
                    sx={{ height: 52, alignItems: "center" }}
                  >
                    <Box
                      display="grid"
                      gridTemplateColumns="50px repeat(6, 1fr) 30px"
                      gap={1}
                      width="100%"
                      alignItems="center"
                    >
                      <Avatar src={membership.companyLogo ?? logo} alt={membership.company} />
                      <ListItemText primary={membership.company} />
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {membership.role}
                            {membership.role === "Owner" && <StarIcon width={18} color="gold" />}
                          </Box>
                        }
                      />
                      <ListItemText
                        primary={
                          <Label variant="soft" color={STATUS_COLORS[membership.status] ?? "default"}>
                            {membership.status}
                          </Label>
                        }
                      />
                      <ListItemText primary={membership.contactName} />
                      <ListItemText primary={membership.contactEmail} />
                      <ListItemText primary={membership.contactPhone} />

                      <Box display="flex" justifyContent="center">
                        {isCurrent && <CheckIcon width={20} color={success.main} />}
                      </Box>
                    </Box>
                  </ListItemButton>

                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5} width="100px">
                    {membership.role === "Owner" && (
                      <EditIconButton size={20} onClick={() => onEdit(membership)} tooltip={{ title: t('edit') }} />
                    )}

                    {membership.status === "Pending Confirmation" && (
                      <Tooltip title={t('requestConfirmationEmail')} placement="top" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onResend(membership.id)}
                          disabled={isResending.value}
                        >
                          <OutgoingMailIcon width={20} height={20} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {membership.role !== "Owner" && (
                      <DeleteIconButton size={20} onClick={() => onDelete(membership)} tooltip={{ title: t('delete') }} />
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </List>
      </Box> */}
    </>
  );
}

const StyledTableCell = styled(TableCell)({
  wordBreak: 'break-all'
})